<template>
    <div class="lottery-numbers-picker" data-test="numbers-board" :data-testid="dataTestid">
        <label
            v-for="number in boardNumbers"
            :key="`primary-${number}`"
            class="lottery-numbers-picker__cell"
            :data-testid="`${number}${modelValue.includes(number) ? '-selected' : ''}`"
            @click.prevent="preventOverfill(number)"
            @keydown.enter.passive="handleKeydown(number)"
        >
            <input
                class="lottery-numbers-picker__cell-checkbox"
                type="checkbox"
                :name="number.toString()"
                :value="number"
                v-model="modelValue"
                data-test="checkbox-number"
                :data-test-value="number + numbersOffset"
            />
            <span
                class="lottery-numbers-picker__cell-number-wrapper"
                :class="[
                    { 'lottery-numbers-picker__cell-number-wrapper--hot': hotNumbers?.includes(number) },
                    { 'lottery-numbers-picker__cell-number-wrapper--cold': coldNumbers?.includes(number) },
                    {
                        'lottery-numbers-picker__cell-number-wrapper--active':
                            modelValue.includes(number) && multipliersFormatted.has(number),
                    },
                ]"
            >
                <span class="lottery-numbers-picker__cell-number" data-test="number">{{ number + numbersOffset }}</span>
                <span v-if="multipliersFormatted.has(number)" class="lottery-numbers-picker__cell-multiplier">{{
                    multipliersFormatted.get(number)
                }}</span>
            </span>
        </label>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { KamenyBetOption } from "../data/kameny-constants";

const emit = defineEmits(["numberPicked"]);

const filling = defineModel<string>("filling", { required: false });

const props = withDefaults(
    defineProps<{
        maxBoardSize: number;
        minBoardSize?: number;
        numbersCount: number;
        numbersOffset?: number;
        hotNumbers?: number[];
        coldNumbers?: number[];
        multipliers?: KamenyBetOption[];
        dataTestid?: string;
    }>(),
    {
        numbersOffset: 0,
    }
);

const modelValue = defineModel<number[]>();

const boardNumbers = computed(() => Array.from({ length: props.numbersCount }, (_, index) => index + 1));

const isFull = computed(() => modelValue.value?.length >= props.maxBoardSize);

const isValid = computed(() => {
    if (props.minBoardSize) {
        return props.minBoardSize <= modelValue.value?.length && props.maxBoardSize >= modelValue.value?.length;
    }

    return isFull.value;
});

const preventOverfill = (numberToAdd: number) => {
    const index = modelValue.value.indexOf(numberToAdd);

    if (isFull.value && index === -1) {
        return;
    }

    if (index !== -1) {
        modelValue.value.splice(index, 1);
    } else {
        modelValue.value.push(numberToAdd);
    }

    emit("numberPicked");
    filling.value = "manual";
};

const handleKeydown = (numberToAdd: number) => {
    let modified = false;
    if (modelValue.value.includes(numberToAdd)) {
        modelValue.value = modelValue.value.filter((number) => number !== numberToAdd);

        modified = true;
    } else if (!isFull.value) {
        modelValue.value = [...modelValue.value, numberToAdd];

        modified = true;
    }

    if (modified) {
        filling.value = "manual";
    }
};

const multipliersFormatted = computed(() => {
    const formatted = new Map<number, string>();

    if (props.multipliers) {
        props.multipliers.forEach((item) => {
            formatted.set(item.id, `${item.multiplier.toLocaleString("en").replace(/,/g, "\u00A0")}x`);
        });
    }

    return formatted;
});

defineExpose({ isFull, isValid });
</script>
